import { useEffect, useState } from "react";
import { fetchLocalSetup, type LocalSetup } from "../api.js";
import { useSession } from "../data/usePage.js";
import { SystemPage, SystemSection } from "./SystemPage.js";

/**
 * "Run datawiki locally" — instructions for `npx datawiki` against this server.
 * Now an SPA system page (was server-rendered): the command is computed by
 * GET /api/local from the server's url + the picked repo + its committed
 * `.datawiki.json`. The repo dropdown chooses which content repo to set up.
 */
export function LocalView() {
  const session = useSession();
  const repos = session.status === "ready" ? session.data.repos : [];
  const [repo, setRepo] = useState("");
  const [setup, setSetup] = useState<LocalSetup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Default the selection to the first repo once the session loads.
  useEffect(() => {
    if (!repo && repos.length > 0) setRepo(repos[0].name);
  }, [repo, repos]);

  useEffect(() => {
    if (!repo) return;
    setSetup(null);
    fetchLocalSetup(repo)
      .then(setSetup)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [repo]);

  if (error)
    return (
      <SystemPage title="Run locally">
        <pre className="dw-error">{error}</pre>
      </SystemPage>
    );
  if (!setup)
    return (
      <SystemPage title="Run locally">
        <p className="dw-muted">Loading…</p>
      </SystemPage>
    );

  const block = setup.steps.join("\n");
  const copy = (): void => {
    navigator.clipboard.writeText(block).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };

  return (
    <SystemPage
      title="Run locally"
      lead="Render and edit this wiki on your machine. Content lives in your checkout — edits, commits and pushes use your own git credentials. Data queries are served by this datawiki, so no local database access is needed."
    >
      {setup.mode === "mismatch" && setup.configuredRemote && (
        <p className="dw-notice">
          Heads up: this repo's <code>.datawiki.json</code> points at{" "}
          <code>{setup.configuredRemote}</code>, not this server (
          <code>{setup.ourUrl}</code>). The <code>--remote</code> flag below
          overrides it for this run.
        </p>
      )}
      <SystemSection title="Setup">
        {repos.length > 1 && (
          <p className="dw-local-repo">
            <label>
              Repo:{" "}
              <select value={repo} onChange={(e) => setRepo(e.target.value)}>
                {repos.map((r) => (
                  <option key={r.name} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
          </p>
        )}
        <ol className="dw-setup-steps">
          {setup.repoUrl && <li>Clone the repo and step in.</li>}
          <li>
            Run the installer below — it points npm at this server's registry and
            installs the <code>datawiki</code> command (globally, or per-run via
            npx). Then run <code>datawiki serve --remote {setup.ourUrl}</code> to boot a
            local server and open your browser.
          </li>
        </ol>
        <div className="dw-cmd">
          <button className="dw-cmd-copy" onClick={copy}>
            {copied ? "copied" : "copy"}
          </button>
          <pre>
            <code>{block}</code>
          </pre>
        </div>
        <p className="dw-muted">Requires Node &gt;= 22.</p>
      </SystemSection>
      <SystemSection title="No install (npx)">
        <p className="dw-muted">
          Run it once without installing anything. The <code>--registry</code>{" "}
          flag routes only <code>@datawiki/*</code> to this server.
        </p>
        <div className="dw-cmd">
          <pre>
            <code>{setup.npxCmd}</code>
          </pre>
        </div>
      </SystemSection>
    </SystemPage>
  );
}
